import clsx from "clsx";
import { MdSearch } from "react-icons/md";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useEffect, useMemo, useState } from "react";
import { getGroomerColumns } from "@/app/hooks/custom_hooks";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IGroomerStaffListItem } from "@/api_framework/api_modals/Configuration";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useConfigurationGroomerStaffListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationGroomerStaffListAPI";

const GroomerListComponent = () => {
  const { NO_APPOINTMENTS } = useImageConstants();
  const { execute } = useConfigurationGroomerStaffListAPI();
  const [groomers, setGroomers] = useState<IGroomerStaffListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCity, setSelectedCity] = useState("All Cities");
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const {
    register,
    setValue,
    formState: { errors },
  } = useForm<{ city: string }>({
    defaultValues: { city: "All Cities" },
    mode: "onChange",
  });
  const { cities } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const fetchCities = useOrgCitiesListAPI("INVENTORY");
  const columns = getGroomerColumns(navigate, getRouteKey);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const cityOrgItem = useSelectMenuReducer(cities, "name", "id", {
    withAllSelectOption: true,
    allSelectItemOptions: { title: "All Cities" },
  });

  useEffect(() => {
    setValue("city", selectedCity, { shouldValidate: true });
  }, [selectedCity, setValue]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    execute()
      .then((data) => {
        if (isMounted) {
          setGroomers(data);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [execute]);

  const filteredGroomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return groomers.filter((groomer) => {
      const matchesQuery =
        !query ||
        [groomer.name, groomer.groomer_id, groomer.phone_number, groomer.email, groomer.truein_identifier]
          .filter(Boolean)
          .some((value) => value.toString().toLowerCase().includes(query));
      const cityValue = groomer.city || groomer.city_name || "";
      const matchesCity = selectedCity === "All Cities" || cityValue.toLowerCase() === selectedCity.toLowerCase();
      return matchesQuery && matchesCity;
    });
  }, [groomers, searchQuery, selectedCity]);

  return (
    <div className="px-5 py-3">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-sm font-semibold text-slate-900">Groomer staff Setup</h1>
          <p className="mt-1 text-xs text-[#6C727F]">A list of all the groomers from every clinic.</p>
        </div>
        <div className="sm:flex-none mt-4 sm:mt-0 sm:ml-16">
          <SolidButton size="2XL" onButtonClick={() => navigate(getRouteKey("GROOMER_SETUP_ONBOARD", "url"))}>
            Add Groomer
          </SolidButton>
        </div>
      </div>
      <div className="mt-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex w-full flex-col gap-2 sm:max-w-xl">
            <div className="flex items-center gap-2">
              <div className="relative w-2/3">
                <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      // trigger filter immediately; state update already handled
                    }
                  }}
                  placeholder="Search Groomer Name, ID"
                  className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-10 text-sm text-slate-900 shadow-sm focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#1F1F23]"
                    onClick={() => setSearchQuery("")}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="w-full sm:w-56">
            <NonSearchableSelectMenu
              errorMessages={[{ message: "", type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  setSelectedCity(item.title);
                  setValue("city", item.title, { shouldValidate: true });
                }
              }}
              fieldError={errors?.city || null}
              register={register("city", { required: true })}
              selectItems={cityOrgItem}
              placeholder="Select City"
              showTooltips={false}
              showDropdownIcon={true}
              defaultSelected={cityOrgItem.find((c) => c.title === selectedCity)}
              listBoxClassName="w-full"
              className={clsx("text-gray-800")}
              containerClassName="w-full"
            />
          </div>
        </div>
        <div className="mt-3">
          {isLoading ? (
            <>
              <SkeletonType1 />
              <SkeletonType1 className="mt-4" />
            </>
          ) : filteredGroomers.length === 0 ? (
            <NoDataFoundComponent
              imageURL={NO_APPOINTMENTS}
              message="No Groomer Data"
              description="No groomers have been found, please onboard a few to see in the list."
            />
          ) : (
            <div>
              <DataTable data={filteredGroomers || []} columns={columns} maxHeight="600px" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroomerListComponent;
